import LinkIcon from '@/features/analytics/invite-links/icons/stones/Link.svg?react'

import { MarkingPartLayout } from '../shared/MarkingPartLayout'

import { AnalyticsTableRowType } from '@/features/analytics/types'
import { ShareIcon } from '@/shared/icons'
import { Spinner } from '@/shared/ui'

import { useInviteLinksStore } from '@/features/analytics/model'
import { ShareCreative } from '@/features/creative/share'
import { useGetContragents, UseSavedErid } from '@/shared/api'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import {
    linkMethods,
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'
import cl from 'clsx'
import { useMemo } from 'react'
import { MARKING_DIALOG_LAYOUT } from '../shared/MarkingPartLayout/data'
import classes from './EridLink.module.scss'

import { MarkingSettings } from './ui/MarkingSettings'
import { coerceColor } from './utils'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'
import type { ICreativeWithMarkableFile, FontFamily } from '../MarkingEditor/types'
import { useMarkingPersistence } from '../MarkingEditor/hooks'
import { LinkView } from './ui/LinkView'

const { pickWwld } = workspaceWithLinkMethods
const { pickLd } = linkMethods

const STYLE = {
    toast: {
        left: 'auto',
        right: 0,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-template-expression
        transformOrigin: `-${MARKING_DIALOG_LAYOUT.totalWidth / 2}px ${0}px`,
    },
    linkView: {
        maxWidth:
            MARKING_DIALOG_LAYOUT.whiteColumn.width -
            MARKING_DIALOG_LAYOUT.whiteColumn.paddingRight,
    },
}

const useTelegramMarkingData = ({
    savedErid,
    adOrdContragentId,
    markingFormData,
    ordAdditionalData,
    ...wwld
}: EridLinkProps) => {
    // Деструктурируем базовые поля
    const { mark_media, mark_media_color, mark_text, mark_media_text } =
        markingFormData

    // Новые поля (когда будут сохранены в БД) - используем опциональный доступ
    // так как они еще не добавлены в тип ORDMarkingFormData (будет в SYN-829)
    const extendedData = markingFormData as typeof markingFormData & {
        mark_media_font_size?: number | null
        mark_media_alignment?: string | null
        mark_media_font_family?: string | null
        mark_media_has_background?: boolean | null
        mark_media_background_color?: string | null
        mark_media_position_x?: number | null
        mark_media_position_y?: number | null
        // Информация о масштабе для пересчета шрифта и позиций при экспорте
        mark_media_canvas_width?: number | null
        mark_media_canvas_height?: number | null
        mark_media_original_width?: number | null
        mark_media_original_height?: number | null
    }

    const mark_media_font_size = extendedData.mark_media_font_size
    const mark_media_alignment = extendedData.mark_media_alignment
    const mark_media_font_family = extendedData.mark_media_font_family
    const mark_media_has_background = extendedData.mark_media_has_background
    const mark_media_background_color = extendedData.mark_media_background_color
    const mark_media_position_x = extendedData.mark_media_position_x
    const mark_media_position_y = extendedData.mark_media_position_y
    const mark_media_canvas_width = extendedData.mark_media_canvas_width
    const mark_media_canvas_height = extendedData.mark_media_canvas_height
    const mark_media_original_width = extendedData.mark_media_original_width
    const mark_media_original_height = extendedData.mark_media_original_height
    const { activeAwd } = useActiveWorkspace()
    // Для маркировки нужен рекламодатель, а не издатель
    // Используем adOrdContragentId из ordAdditionalData (рекламодатель), а не из markingFormData (издатель)
    const advertiserContragentId = ordAdditionalData?.adOrdContragentId

    // Запрашиваем контрагентов с ролью 'advertiser' для поиска рекламодателя
    const { data: contragents } = useGetContragents({
        ...activeAwd,
        roles: ['advertiser'],
    })

    // Загружаем настройки редактора из БД и localStorage
    // Используем useMarkingPersistence для правильной загрузки данных из БД
    const fullWwld = useMemo(() => {
        return { ...activeAwd, ...pickLd(wwld) } as WorkspaceWithLinkDescriptor
    }, [activeAwd, wwld])

    const persistence = useMarkingPersistence(fullWwld)
    const localEditorData = useMemo(() => {
        if (!fullWwld.link_id) return null
        return persistence.loadMarkingSettings()
    }, [fullWwld, persistence.loadMarkingSettings, persistence.savedData])

    return useMemo(() => {
        if (!(contragents && savedErid && advertiserContragentId)) {
            return undefined
        }

        // Ищем рекламодателя по ID из ordAdditionalData
        const advertiser = contragents.find((ca) => {
            const caId = ca.ord_contragent_id
            const searchId = advertiserContragentId
            return caId === searchId || Number(caId) === Number(searchId)
        })

        if (advertiser) {
            const {
                inn,
                name,
                //  full_name
                type,
            } = advertiser
            // const effectiveName = full_name ? full_name : name
            const effectiveName = name

            if (inn && effectiveName) {
                // Объединяем данные из БД и localStorage: приоритет у данных из БД
                // Если в БД нет данных, используем данные из localStorage
                const finalFontSize =
                    mark_media_font_size ?? localEditorData?.fontSize
                const finalAlignment =
                    mark_media_alignment ?? localEditorData?.alignment
                const finalFontFamily =
                    mark_media_font_family ?? localEditorData?.fontFamily
                const finalHasBackground =
                    mark_media_has_background ?? localEditorData?.hasBackground
                const finalBackgroundColor =
                    mark_media_background_color ??
                    localEditorData?.backgroundColor
                const finalPositionX =
                    mark_media_position_x ?? localEditorData?.position?.x
                const finalPositionY =
                    mark_media_position_y ?? localEditorData?.position?.y
                
                // Собираем scale из БД или localStorage
                const finalScale =
                    mark_media_canvas_width &&
                    mark_media_canvas_height &&
                    mark_media_original_width &&
                    mark_media_original_height
                        ? {
                              canvasWidth: mark_media_canvas_width,
                              canvasHeight: mark_media_canvas_height,
                              originalWidth: mark_media_original_width,
                              originalHeight: mark_media_original_height,
                          }
                        : localEditorData?.scale ?? null

                const result = {
                    erid: savedErid,
                    inn,
                    advertiser: effectiveName,
                    type: type ?? ('physical' as const),
                    mark_media,
                    mark_media_color,
                    mark_text,
                    mark_media_text: mark_media_text ?? undefined,
                    // Новые поля: используем данные из БД или localStorage
                    mark_media_font_size:
                        finalFontSize !== undefined && finalFontSize !== null
                            ? finalFontSize
                            : undefined,
                    mark_media_alignment: finalAlignment
                        ? (finalAlignment as
                              | 'left'
                              | 'center'
                              | 'right'
                              | undefined)
                        : undefined,
                    mark_media_font_family: finalFontFamily
                        ? (finalFontFamily as FontFamily | undefined)
                        : undefined,
                    mark_media_has_background:
                        finalHasBackground !== undefined &&
                        finalHasBackground !== null
                            ? finalHasBackground
                            : undefined,
                    mark_media_background_color:
                        finalBackgroundColor !== undefined &&
                        finalBackgroundColor !== null
                            ? finalBackgroundColor
                            : undefined,
                    mark_media_position_x:
                        finalPositionX !== undefined && finalPositionX !== null
                            ? finalPositionX
                            : undefined,
                    mark_media_position_y:
                        finalPositionY !== undefined && finalPositionY !== null
                            ? finalPositionY
                            : undefined,
                    mark_media_scale: finalScale,
                }
                return result
            }
        }

        return undefined
    }, [
        advertiserContragentId,
        contragents,
        savedErid,
        mark_media,
        mark_media_color,
        mark_text,
        mark_media_text,
        mark_media_font_size,
        mark_media_alignment,
        mark_media_font_family,
        mark_media_has_background,
        mark_media_background_color,
        mark_media_position_x,
        mark_media_position_y,
        mark_media_canvas_width,
        mark_media_canvas_height,
        mark_media_original_width,
        mark_media_original_height,
        localEditorData,
    ])
}

// const ZERO_WIDTH_SPACE = String.fromCodePoint(0x200b)

export type EridLinkProps = {
    groupId: number
    markingFormData: ORDMarkingFormData
    tableRowData: AnalyticsTableRowType
    adOrdContragentId: number
    ordAdditionalData?: { adOrdContragentId: number | null } | null
} & Pick<UseSavedErid, 'savedErid'> &
    WorkspaceWithLinkDescriptor
export const EridLink = (props: EridLinkProps) => {
    const { activeWorkspace } = useActiveWorkspace()

    const { markingFormData, tableRowData, savedErid } = props

    const telegramMarkingData = useTelegramMarkingData(props)

    const { eridError, setEridError } = useInviteLinksStore()
    const setEditErrorBounded = () => setEridError(true)

    const hasCreativeFlag = markingFormData.creative_flag

    const hasSelectedMarkingCheckbox =
        markingFormData.mark_media || markingFormData.mark_text

    return (
        <MarkingPartLayout
            title={'Маркировка публикации'}
            Icon={LinkIcon}
            tooltipContent={'Маркировка публикации'}
            flagValue={markingFormData.creative_flag}
        >
            <div className={classes.content}>
                <MarkingSettings
                    {...pickWwld(props)}
                    markMedia={markingFormData.mark_media}
                    markText={markingFormData.mark_text}
                    markMediaColor={coerceColor(
                        markingFormData.mark_media_color
                    )}
                    markMediaText={markingFormData.mark_media_text}
                    creative={
                        props.tableRowData.activeCreative as
                            | ICreativeWithMarkableFile
                            | null
                            | undefined
                    }
                    markingFormData={markingFormData}
                    disabled={Boolean(tableRowData.publisher?.telegram_post_id)}
                    hasError={Boolean(eridError) && !hasSelectedMarkingCheckbox}
                />

                {activeWorkspace.workspace_type === 'site' &&
                    'site_id' in tableRowData.source && (
                        <LinkView
                            groupId={props.groupId}
                            disabled={Boolean(
                                tableRowData.publisher?.telegram_post_id
                            )}
                            activeSite={activeWorkspace}
                            utmLink={tableRowData.source.utmLink}
                            siteLink={tableRowData.source}
                            erid={savedErid ?? undefined}
                        />
                    )}

                <ShareCreative
                    {...pickLd(props)}
                    {...(hasSelectedMarkingCheckbox && hasCreativeFlag
                        ? {}
                        : {
                              disabled: setEditErrorBounded,
                          })}
                    highlight={hasCreativeFlag}
                    marking={telegramMarkingData}
                    markableFile={
                        props.tableRowData.activeCreative?.markableFile
                    }
                    toastStyle={STYLE.toast}
                    withTooltip={!hasCreativeFlag}
                >
                    {({ isLoading }) => (
                        <div
                            className={cl(classes.shareButton, {
                                [classes.highlight]: hasCreativeFlag,
                            })}
                        >
                            Отправить пост себе с маркировкой
                            {isLoading ? (
                                <Spinner size={16} strokeWidth={2} />
                            ) : (
                                <ShareIcon className={classes.icon} />
                            )}
                        </div>
                    )}
                </ShareCreative>
            </div>
        </MarkingPartLayout>
    )
}
